import React, { useMemo, useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty } from 'lodash'

import WidgetContext from '@/components/widget/widget.context'
import { CHART } from '@/components/charts/chartPalette'
import WidgetContainer from '@/components/widget/WidgetContainer'
import WidgetTooltip from '@/components/widget/WidgetTooltip'
import BarChart from '@/components/charts/BarChart'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { formatPercent } from '@/utils/helpers'

import { formatUsageByOrganization } from './widgetHelpers'

export const OrganizationComparisonWidget = ({ loadingKey = 'checklistPage' }) => {
  const [activePage, setActivePage] = useState(1)
  const { data, loading } = useSelector((state) => state.analytics)
  const { isPercent, handleChangeCsvData, handleResetWidgetFilters } = useContext(WidgetContext)

  const tableData = useMemo(() => {
    return formatUsageByOrganization(data.checklistUsageByOrganization, isPercent)
  }, [data.checklistUsageByOrganization, isPercent])

  const columns = useMemo(
    () => [
      {
        accessor: 'organization',
        label: 'Organization',
        noWrap: true,
      },
      {
        label: 'Completion %',
        accessor: 'percent',
        format: (value) => formatPercent(value),
      },
      {
        label: 'Completed Items',
        accessor: 'count',
        format: (value) => Number(value).toLocaleString(),
      },
      {
        label: 'Total Items',
        accessor: 'total',
        format: (value) => Number(value).toLocaleString(),
      },
    ],
    []
  )

  const [rowsPerPage, setRowsPerPage] = useState(20)

  const handleUpdateRowsPerPage = (value) => {
    setActivePage(1)
    setRowsPerPage(value)
  }
  const count = tableData.length
  const totalPages = Math.ceil(count / rowsPerPage)
  const paginatedRows = useMemo(
    () => tableData.slice((activePage - 1) * rowsPerPage, activePage * rowsPerPage).reverse(),
    [tableData, activePage, rowsPerPage]
  )

  useEffect(() => {
    handleChangeCsvData({ headers: columns, data: tableData })
  }, [tableData])

  useEffect(() => {
    handleResetWidgetFilters()
  }, [data.checklistUsageByOrganization])

  return (
    <WidgetContainer
      medium
      withPagination
      loading={loading[loadingKey]}
      noData={isEmpty(data.checklistUsageByOrganization)}
      table={
        <AdvancedTable
          index="organization"
          defaultOrderBy="organization"
          rows={tableData}
          columns={columns}
          rowsPerPage={rowsPerPage}
          pagination
          enableUpdateRowsPerPage
          handleUpdateRowsPerPage={handleUpdateRowsPerPage}
          totalPages={totalPages}
        />
      }
      chart={
        <BarChart
          layout="horizontal"
          enableGridX
          data={paginatedRows}
          padding={0.3}
          keys={['dataKey']}
          indexBy="organization"
          tooltip={(item) => (
            <WidgetTooltip color={item.color}>
              <h4>{item.data.organization}</h4>
              <p>{`${formatPercent(item.data.percent)} average checklist completion`}</p>
              <p>
                {`${Number(item.data.count).toLocaleString()} of ${Number(item.data.total).toLocaleString()} items completed`}
              </p>
            </WidgetTooltip>
          )}
          colorBy="id"
          colors={[CHART.INFORMATIVE]}
          axisBottom={{
            orient: 'bottom',
            tickSize: 5,
            padding: 0.5,
            tickPadding: 5,
            tickRotation: 0,
            format: (value) => (isPercent ? formatPercent(value) : Number(value).toLocaleString()),
          }}
          axisLeft={{
            orient: 'left',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
          }}
          margin={{ bottom: 50, left: 150, right: 50, top: 20 }}
        />
      }
    />
  )
}
